import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useEffect, useState } from 'react';

import Link from '~/common/components/Link';
import { useSnackbar } from '~/common/components/Snackbar/snackbarStore';
import apiEndpoints from '~/common/constants/api-endpoints';
import useUrl from '~/common/hooks/useUrl';
import { typedApiClient } from '~/common/util/api-client';
import { createBlobUrl } from '~/common/util/pdfUtils';

type DownloadLinkProps = {
  apiError?: string;
  documentId?: number | string;
  icon?: any;
  text?: string;
  variant?: any;
  linkPath?: string;
  sx?: any;
} & ({ documentId: number | string } | { linkPath: string });

const DownloadLink = ({
  apiError,
  documentId,
  icon,
  text,
  variant,
  sx,
  linkPath = '#',
}: Readonly<DownloadLinkProps>) => {
  const { obfuscatedLoanIdentifier } = useUrl();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isError, setIsError] = useState(false);
  const showSnackbar = useSnackbar();

  useEffect(() => {
    setIsDownloading(false);
    if (isError) {
      showSnackbar({ message: apiError, severity: 'error' });
    }
    // eslint-disable-next-line @eslint-react/exhaustive-deps
  }, [isError]);

  const retrievePdf = async (documentId: number) => {
    setIsDownloading(true);
    const { obfuscatedLenderDatabaseId, obfuscatedLoanRecordId } =
      obfuscatedLoanIdentifier;
    const pdfEndpoint = `${apiEndpoints.classifyDocuments.GET_PDF}/${obfuscatedLenderDatabaseId}/${obfuscatedLoanRecordId}/${documentId}`;

    typedApiClient.instance
      .get(pdfEndpoint, {
        responseType: 'arraybuffer',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/pdf',
        },
      })
      .then(({ data }) => createBlobUrl(data))
      .then((blobUrl) => {
        window.open(blobUrl, '__blank');
        setIsError(false);
      })
      .catch(() => {
        setIsError(true);
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  return (
    <Link
      href={linkPath}
      variant={variant}
      fontSize="md"
      color={isDownloading ? 'uwmOrange.500' : 'blue.700'}
      sx={sx}
      onClick={async (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (linkPath && linkPath !== '#') {
          window.open(linkPath, '_blank', 'noopener,noreferrer');
        } else {
          await retrievePdf(+documentId);
        }
      }}
      fontWeight="400"
    >
      {isDownloading && 'Downloading...'}
      {!isDownloading && (
        <>
          {text} <FontAwesomeIcon icon={icon} style={{ marginLeft: '4px' }} />
        </>
      )}
    </Link>
  );
};

/* c8 ignore next */
DownloadLink.defaultProps = {
  apiError: `We've encountered an error. Please refresh and try again. If the error persists, contact your AE for assistance.`,
  downloadError: `We've encountered an error. Please refresh and try again. If the error persists, contact your AE for assistance.`,
  handleDownload: (url: string, target: string) => {
    window.open(url, target);
  },
  file: 'File',
  icon: faExternalLinkAlt,
  target: '_blank',
  text: 'Download',
  variant: 'link',
};

export default DownloadLink;
